import React, { useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import PageMeta from "@/components/common/PageMeta";
import Pagination from "@/components/common/Pagination";
import Button from '@/components/ui/button/Button';
import CarModelCreateForm from '@/features/car-models/components/CarModelCreateForm';
import { CarModelsTable } from "@/features/car-models/components/CarModelsTable";
import { useCarModels, CAR_MODELS_PER_PAGE, useCreateCarModel, useUploadCarModelImage } from '@/features/car-models/hooks/useCarModels';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ModalWithSearchParam } from '@/components/ui/modal/ModalWithSearchParam';
import { useModalSearchParam } from '@/shared/hooks/useModalSearchParam';
import { CreateCarModelDto } from '@/features/car-models/api/carModelsApi';
import { PlusIcon } from '@/shared/icons';
import { TableColumnsFilter } from '@/components/ui/table/TableColumnsFilter';
import { ColumnFilter } from '@/components/ui/table/ColumnFilter';
import { Loader } from "@/components/ui/loader/Loader";


// Ключ для хранения настроек видимости столбцов
const CAR_MODELS_TABLE_STORAGE_KEY = 'car-models-table-columns';

// Столбцы, видимые по умолчанию
const DEFAULT_VISIBLE_COLUMNS = ['image', 'model', 'status', 'description', 'actions'];

const CarModelsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;
  const { data, isLoading } = useCarModels(currentPage);
  const { isOpen, open: openModal, close: closeModalWithoutCheck } = useModalSearchParam({
    paramName: 'create'
  });
  const createCarModel = useCreateCarModel();
  const uploadImage = useUploadCarModelImage();
  const formRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);
  const navigate = useNavigate();

  // Определяем столбцы для таблицы моделей автомобилей
  const CAR_MODEL_COLUMNS = [
    { key: 'image', header: t('car_models.columns.image'), field: 'image' },
    { key: 'model', header: t('car_models.columns.model'), field: 'model' },
    { key: 'status', header: t('car_models.columns.status'), field: 'is_active' },
    { key: 'description', header: t('car_models.columns.description'), field: 'description_en' },
    { key: 'actions', header: t('car_models.columns.actions'), field: 'actions' },
  ];

  const handleModalClose = useCallback(() => {
    formRef.current?.handleClose();
  }, []);

  const handleFormClose = useCallback((hasUnsavedChanges?: boolean) => {
    if (hasUnsavedChanges === false) {
      closeModalWithoutCheck();
    }
  }, [closeModalWithoutCheck]);

  const handleCreateModel = async (modelData: CreateCarModelDto, image?: File) => {
    try {
      await createCarModel.mutateAsync({ data: modelData, image });
      closeModalWithoutCheck();
    } catch (error) {
      throw error;
    }
  };

  return (
    <>
      <PageMeta
        title={t('car_models.title')}
        description={t('car_models.description')}
      />
      <PageBreadcrumb
        items={[
          { label: t('nav.car_models'), path: '/models' }
        ]}
      />

      <TableColumnsFilter
        columns={CAR_MODEL_COLUMNS}
        storageKey={CAR_MODELS_TABLE_STORAGE_KEY}
        defaultColumns={DEFAULT_VISIBLE_COLUMNS}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <ColumnFilter
              size="sm"
              buttonText={t('filters.columns')}
              className="w-auto"
            />
          </div>
          <Button
            size="sm"
            variant="primary"
            startIcon={<PlusIcon />}
            onClick={openModal}
          >
            {t('car_models.add_new')}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <Loader />
          </div>
        ) : (
          <>
            <CarModelsTable carModels={data?.data || []} isLoading={false} />
            <div className="mt-4 flex justify-end">
              <Pagination
                totalItems={data?.total || 0}
                itemsPerPage={CAR_MODELS_PER_PAGE}
                currentPage={currentPage}
              />
            </div>
          </>
        )}
      </TableColumnsFilter>

      <ModalWithSearchParam
        paramName="create"
        maxWidth="800px"
        className="p-6"
        onClose={handleModalClose}
      >
        <CarModelCreateForm
          ref={formRef}
          onSubmit={handleCreateModel}
          onClose={handleFormClose}
        />
      </ModalWithSearchParam>
    </>
  );
};

export default CarModelsPage;
